import {ModalType, SnackbarType} from '@treez-ui/core/feedback/modals';
import {
    PriceTierWithHasProductsFlag,
    PriceTierThresholdType,
} from '@treez/commons/sharedTypings/priceTier';
import {OwnerType} from '@treez/commons/sharedTypings/product';
import {ACCEPTED, CREATED} from 'http-status-codes';
import _ from 'lodash';
import React, {FunctionComponent, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {RouteComponentProps} from 'react-router';
import {PriceTierWithCatalogPresence} from '~/lib/sharedInterfaces';
import EditPriceTierForm, {PriceTierEditMode} from '~/modules/forms/EditPriceTierForm';
import {updatePriceTiers} from '~/redux/actions/OrganizationActions';
import {clearSnackbar, showSnackbar} from '~/redux/actions/SnackbarActions';
import {
    closeFeedbackModal,
    openFeedbackModal,
} from '~/redux/boundActionCreators/UserFeedbackActions';
import {
    selectOrganizationCatalogId,
    selectPriceTiersForOrganization,
} from '~/redux/selectors/organizationSelectors';
import {createPriceTier, getPriceTier, updatePriceTier} from '~/utils/ProductAPI';
import UIWrapper from '../UIWrapper';
import PriceTierReassignModal from './PriceTierReassignModal';

interface RouteParams {
    id: string;
    organizationId: string;
}

export const EditPriceTier: FunctionComponent<
    RouteComponentProps<RouteParams, {}, PriceTierWithCatalogPresence>
> = (props: RouteComponentProps<RouteParams, {}, PriceTierWithCatalogPresence>) => {
    const {
        match: {
            params: {organizationId: ownerId},
        },
        history,
        location: {state: priceTierToEdit},
    } = props;

    const editMode = _.isUndefined(priceTierToEdit)
        ? PriceTierEditMode.Add
        : PriceTierEditMode.Edit;

    const dispatch = useDispatch();

    const priceTiersGlobal = useSelector(selectPriceTiersForOrganization);
    const organizationCatalogId: number = useSelector(selectOrganizationCatalogId);

    const [isPriceTierReassignOpen, setIsPriceTierReassignOpen] = useState<boolean>(false);
    const [deactivateOverride, setDeactivateOverride] = useState<boolean>(false);

    const handleCancel = (draftPriceTier: Partial<PriceTierWithCatalogPresence>) => {
        if (_.isEqual(priceTierToEdit, draftPriceTier)) {
            history.goBack();
        } else {
            openFeedbackModal(
                () => {
                    closeFeedbackModal();
                    history.goBack();
                },
                'PriceTier Information Will Be Lost',
                {
                    type: ModalType.WARNING,
                    bodyText:
                        'If you exit now, the price tier information you have entered will be lost',
                    cancelOption: true,
                    onCancel: () => closeFeedbackModal(),
                }
            );
        }
    };

    const handleSave = async (draftPriceTier: Partial<PriceTierWithCatalogPresence>) => {
        if (editMode === PriceTierEditMode.Edit) {
            if (
                priceTierToEdit?.isActive &&
                !draftPriceTier.isActive &&
                priceTierToEdit.hasProducts &&
                !deactivateOverride
            ) {
                openFeedbackModal(() => onReassignModalOpen(), 'Price Tier Cannot Be Deactivated', {
                    type: ModalType.WARNING,
                    bodyText:
                        'This price tier cannot be deactivated because it has products currently assigned to it. Do you want to reassign those products to a different price tier?',
                    cancelOption: true,
                    onCancel: () => closeFeedbackModal(),
                    buttonText: 'Yes',
                });
                return;
            }

            let response;
            try {
                response = await updatePriceTier(draftPriceTier, organizationCatalogId);
            } catch (error) {
                console.error(error);
                openFeedbackModal(closeFeedbackModal, 'Error Saving Price Tier', {
                    type: ModalType.WARNING,
                    bodyText: 'There was an error saving the price tier',
                    cancelOption: true,
                    onCancel: () => closeFeedbackModal(),
                });
            }

            if (response && response.status === ACCEPTED) {
                const {
                    data: {data: updatedPriceTier},
                } = response;
                updateGlobalPriceTiers(updatedPriceTier, false);
                dispatch(
                    showSnackbar({
                        onClose: () => {
                            dispatch(clearSnackbar());
                        },
                        type: SnackbarType.SUCCESS,
                        content: `Saved!`,
                    })
                );
                history.goBack();
            }
        } else {
            const priceTierToSave = {
                ...draftPriceTier,
                ownerId,
                ownerType: OwnerType.ORG,
                thresholdType: PriceTierThresholdType.FLAT,
            };

            let response;
            try {
                response = await createPriceTier(priceTierToSave, organizationCatalogId);
            } catch (error) {
                console.error(error);
                openFeedbackModal(closeFeedbackModal, 'Error Saving Price Tier', {
                    type: ModalType.WARNING,
                    bodyText: 'There was an error saving the price tier',
                    cancelOption: true,
                    onCancel: () => closeFeedbackModal(),
                });
            }

            if (response && response.status === CREATED) {
                const {
                    data: {data: newPriceTier},
                } = response;
                updateGlobalPriceTiers(newPriceTier, true);

                dispatch(
                    showSnackbar({
                        onClose: () => {
                            dispatch(clearSnackbar());
                        },
                        type: SnackbarType.SUCCESS,
                        content: `Saved!`,
                    })
                );
                history.goBack();
            }
        }
    };

    const updateGlobalPriceTiers = (
        priceTier: PriceTierWithHasProductsFlag,
        isNewTier: boolean
    ) => {
        let restOfPriceTiers = priceTiersGlobal as PriceTierWithHasProductsFlag[];
        if (!isNewTier) {
            restOfPriceTiers = _.reject(priceTiersGlobal, {id: _.get(priceTier, 'id')});
        }

        dispatch(updatePriceTiers([...restOfPriceTiers, priceTier]));
    };

    const onReassignSubmit = () => {
        getPriceTier(priceTierToEdit.id, organizationCatalogId).then((data) => {
            const updatedPriceTier: PriceTierWithHasProductsFlag = data;

            if (updatedPriceTier.hasProducts === false) {
                setDeactivateOverride(true);
            }
        });
    };

    const onReassignModalOpen = () => {
        setIsPriceTierReassignOpen(true);
        closeFeedbackModal();
    };

    const onReassignModalClose = () => {
        setIsPriceTierReassignOpen(false);
    };

    return (
        <UIWrapper>
            <EditPriceTierForm
                priceTier={priceTierToEdit}
                onSave={handleSave}
                onCancel={handleCancel}
                editMode={editMode}
            />
            {isPriceTierReassignOpen && (
                <PriceTierReassignModal
                    onClose={onReassignModalClose}
                    open={isPriceTierReassignOpen}
                    priceTier={priceTierToEdit}
                    onSubmit={onReassignSubmit}
                />
            )}
        </UIWrapper>
    );
};

export default EditPriceTier;
